import { ApplicationRef, Injectable, NgZone } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { BehaviorSubject, Observable, Subscription, from, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, first, scan, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class PromptUpdateService {
  /** Observable que emite true quando há uma nova versão disponível (nunca volta para false sozinho) */
  readonly newVersionAvailable$: Observable<boolean>;

  /** Flag de compatibilidade para uso em templates sem async pipe */
  isNewVersionAvailable = false;

  /** Indica se uma verificação manual está em andamento */
  isChecking = false;

  /** Resultado da última verificação manual (null = nenhuma, true = encontrou, false = já atualizado) */
  lastCheckResult: boolean | null = null;

  private pollingSubscription: Subscription;
  private readonly newVersionSubject = new BehaviorSubject<boolean>(false);

  // Guard consultado antes de QUALQUER aplicação automática de update
  // (visibilitychange e o reload pós-activate). Telas com estado que não
  // pode ser perdido num reload (ex.: login com credenciais digitadas)
  // registram aqui a condição de segurança.
  private autoApplyGuard: (() => boolean) | null = null;

  constructor(
    private appRef: ApplicationRef,
    private swUpdate: SwUpdate,
    private zone: NgZone
  ) {
    this.newVersionAvailable$ = this.newVersionSubject.asObservable().pipe(
      distinctUntilChanged()
    );

    // Sincronizar flag booleana com o observable (dentro da zone para change detection)
    this.newVersionAvailable$.subscribe(available => {
      this.zone.run(() => {
        this.isNewVersionAvailable = available;
      });
    });

    if (this.swUpdate.isEnabled) {
      this.setupPolling();
      this.setupUnrecoverableHandler();
      this.setupVisibilityChangeHandler();
    }
  }

  /**
   * Configura polling único após estabilização do app.
   * Espera o app estabilizar, verifica imediatamente, e depois a cada 15 minutos.
   */
  private setupPolling(): void {
    const appStable$ = this.appRef.isStable.pipe(
      first(isStable => isStable === true)
    );

    this.pollingSubscription = appStable$.pipe(
      switchMap(() => timer(0, 15 * 60 * 1000)), // imediato + a cada 15 min
      switchMap(() =>
        from(this.swUpdate.checkForUpdate()).pipe(
          catchError(err => {
            console.error('Erro ao verificar atualizações:', err);
            return of(false);
          })
        )
      ),
      // scan garante que uma vez true, nunca volta para false
      scan((hasUpdate, checkResult) => hasUpdate || checkResult, false)
    ).subscribe(hasUpdate => {
      if (hasUpdate) {
        this.newVersionSubject.next(true);
        console.log('Nova versão disponível.');
        // Parar polling — não precisa mais verificar
        this.pollingSubscription?.unsubscribe();
      }
    });
  }

  /**
   * Trata o estado irrecuperável do Service Worker.
   * Quando o cache fica inconsistente, força reload para recuperar o app.
   */
  private setupUnrecoverableHandler(): void {
    this.swUpdate.unrecoverable.subscribe(event => {
      console.error('Service Worker em estado irrecuperável:', event.reason);
      // Recarregar automaticamente para recuperar
      document.location.reload();
    });
  }

  /**
   * Aplica atualização automaticamente quando o app volta de background
   * (ex: usuário troca de aba ou minimiza e volta).
   */
  private setupVisibilityChangeHandler(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.isNewVersionAvailable && this.canAutoApply()) {
        // Aplicar atualização silenciosamente ao voltar de background
        this.applyUpdate();
      }
    });
  }

  /** Registra (ou remove, com null) o guard de aplicação automática. */
  setAutoApplyGuard(guard: (() => boolean) | null): void {
    this.autoApplyGuard = guard;
  }

  private canAutoApply(): boolean {
    return !this.autoApplyGuard || this.autoApplyGuard();
  }

  /**
   * Verificação manual disparada pelo usuário (ex: clicar no número da versão).
   * Retorna Promise para que o componente possa reagir ao resultado.
   */
  async checkForUpdate(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) {
      console.log('Service Worker não está habilitado');
      return false;
    }

    if (!('serviceWorker' in navigator)) {
      console.log('Service Worker não suportado neste navegador');
      return false;
    }

    this.isChecking = true;
    this.lastCheckResult = null;

    try {
      // Verificar se já há um SW aguardando ativação
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration?.waiting) {
        this.newVersionSubject.next(true);
        this.isChecking = false;
        this.lastCheckResult = true;
        return true;
      }

      const hasUpdate = await this.swUpdate.checkForUpdate();
      if (hasUpdate) {
        this.newVersionSubject.next(true);
      }

      this.lastCheckResult = hasUpdate;
      console.log(hasUpdate ? 'Nova versão encontrada.' : 'Já está na versão mais recente.');
      return hasUpdate;
    } catch (error) {
      console.error('Erro ao verificar atualizações:', error);
      this.lastCheckResult = false;
      return false;
    } finally {
      this.zone.run(() => {
        this.isChecking = false;
      });
    }
  }

  /**
   * Ativa a nova versão do Service Worker e recarrega a página.
   */
  applyUpdate(): void {
    this.swUpdate.activateUpdate()
      .then(() => {
        // Reavalia o guard após o activate assíncrono: o usuário pode ter
        // começado a digitar nesse intervalo (check→reload não é atômico).
        if (this.canAutoApply()) {
          document.location.reload();
        }
      })
      .catch(error => console.error('Erro ao aplicar atualização:', error));
  }
}
